import React, { useState, useEffect } from "react";
import { customFetch } from "../apiClient";
import Swal from "sweetalert2";

const ReceiptSettings = () => {
  // 1. Unified State for both Footers and Token Settings
  const [settings, setSettings] = useState({
    opdReceiptFooterText: "",
    ipdReceiptFooterText: "",
    billingReceiptFooterText: "",
    pharmacyReceiptFooterText: "",
    labReceiptFooterText: "",
    opdDailySrNoStart: 1, // Default OPD token start
    ipdDailySrNoStart: 1, // Default IPD token start
  });

  const [activeTab, setActiveTab] = useState("opd");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const MAX_CHARS = 500;

  // Tabs mapping for footers
  const tabs = [
    { id: "opd", label: "OPD", key: "opdReceiptFooterText" },
    { id: "ipd", label: "IPD", key: "ipdReceiptFooterText" },
    { id: "billing", label: "Billing", key: "billingReceiptFooterText" },
    { id: "pharmacy", label: "Pharmacy", key: "pharmacyReceiptFooterText" },
    { id: "lab", label: "Laboratory", key: "labReceiptFooterText" },
  ];

  // Fetch Existing Settings
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await customFetch("/Settings");
      if (res.ok) {
        const data = await res.json();
        setSettings({
          opdReceiptFooterText: data.opdReceiptFooterText || "",
          ipdReceiptFooterText: data.ipdReceiptFooterText || "",
          billingReceiptFooterText: data.billingReceiptFooterText || "",
          pharmacyReceiptFooterText: data.pharmacyReceiptFooterText || "",
          labReceiptFooterText: data.labReceiptFooterText || "",
          opdDailySrNoStart: data.opdDailySrNoStart || 1,
          ipdDailySrNoStart: data.ipdDailySrNoStart || 1,
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Input Change for all fields
  const handleInputChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Save Settings
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await customFetch("/Settings", {
        method: "POST",
        body: JSON.stringify(settings), // Sending complete settings object
      });

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: "Hospital settings updated successfully.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      Swal.fire("Error", "Could not save settings.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-center">Loading Settings...</div>;

  const activeKey = tabs.find((t) => t.id === activeTab)?.key;
  const currentText = settings[activeKey] || "";

  return (
    <div className="container mt-4" style={{ maxWidth: "700px" }}>
      <form onSubmit={handleSave}>
        
        {/* CARD 1: Daily Token / Serial No Configuration */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-dark text-white py-2">
            <h5 className="mb-0" style={{ fontSize: "16px" }}>
              <i className="bi bi-123 me-2"></i> Daily Token Setup
            </h5>
          </div>
          <div className="card-body bg-light">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted text-uppercase">
                  OPD Start Number
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white"><i className="bi bi-hash"></i></span>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    placeholder="e.g. 1"
                    value={settings.opdDailySrNoStart || ""}
                    onChange={(e) => handleInputChange("opdDailySrNoStart", e.target.value ? Number(e.target.value) : "")}
                  />
                </div>
                <div className="form-text" style={{ fontSize: "11px" }}>Daily OPD token will start from this number.</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold small text-muted text-uppercase">
                  IPD Start Number
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white"><i className="bi bi-hash"></i></span>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    placeholder="e.g. 100"
                    value={settings.ipdDailySrNoStart || ""}
                    onChange={(e) => handleInputChange("ipdDailySrNoStart", e.target.value ? Number(e.target.value) : "")}
                  />
                </div>
                <div className="form-text" style={{ fontSize: "11px" }}>Daily IPD token will start from this number.</div>
              </div>
            </div>
          </div>
        </div>

        {/* CARD 2: Department Wise Receipt Settings */}
        <div className="card shadow-sm mb-3">
          <div className="card-header bg-primary text-white py-2">
            <h5 className="mb-0" style={{ fontSize: "16px" }}>
              <i className="bi bi-file-earmark-text-fill me-2"></i> Receipt Footers
            </h5>
          </div>
          
          <div className="card-body p-0">
            {/* Bootstrap Tabs Navigation */}
            <ul className="nav nav-tabs px-3 pt-2" style={{ backgroundColor: "#f8f9fa" }}>
              {tabs.map((tab) => (
                <li className="nav-item" key={tab.id} style={{ cursor: "pointer" }}>
                  <span
                    className={`nav-link ${activeTab === tab.id ? "active fw-bold text-primary" : "text-secondary"}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </span>
                </li>
              ))}
            </ul>

            {/* Form Content */}
            <div className="p-3">
              <div className="mb-3">
                <label className="form-label fw-bold small text-muted text-uppercase">
                  {tabs.find((t) => t.id === activeTab)?.label} Footer Content
                </label>
                <textarea
                  className="form-control shadow-none border-secondary-subtle"
                  rows="5"
                  value={currentText}
                  onChange={(e) => handleInputChange(activeKey, e.target.value)}
                  maxLength={MAX_CHARS}
                  placeholder={`Enter terms and conditions for ${tabs.find((t) => t.id === activeTab)?.label} receipts...`}
                  style={{ fontSize: "13px", resize: "none" }}
                ></textarea>
                
                <div className="form-text d-flex justify-content-between mt-1">
                  <span>Maximum {MAX_CHARS} characters allowed.</span>
                  <span className={currentText.length >= MAX_CHARS ? "text-danger fw-bold" : ""}>
                    {currentText.length} / {MAX_CHARS}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Save Button */}
        <div className="d-flex justify-content-end mb-4">
          <button
            type="submit"
            className="btn btn-success px-4 py-2 shadow-sm rounded-pill fw-bold"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span> Saving...
              </>
            ) : (
              <><i className="bi bi-check-circle me-2"></i> Save All Settings</>
            )}
          </button>
        </div>

      </form>

      {/* Info Notice */}
      <div className="alert alert-info border-0 shadow-sm p-3" style={{ fontSize: "13px" }}>
        <div className="d-flex align-items-center">
          <i className="bi bi-info-circle-fill fs-4 text-info me-3"></i>
          <div>
            <strong>Note:</strong> You can manage terms & conditions for each department using the tabs above. Modifying the <strong>Token Start Number</strong> will apply to the very next patient registered today.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptSettings;